(() => {
  'use strict';
  if (window.__uniProgramV6Ready) return;
  window.__uniProgramV6Ready = true;
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  function init(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const on = { signal: controller.signal };
    const allowed = () => !reduced.matches && document.body.dataset.motion !== 'none' && document.body.dataset.motionUser !== 'none' && root.dataset.motion !== 'none' && (!window.UniMotion?.allowed || window.UniMotion.allowed(root));
    const list = root.querySelector('.uni-program-index__links');
    const indicator = root.querySelector('[data-program-index-indicator]');
    const current = root.querySelector('[data-program-index-current]');
    let indicatorFrame = 0;
    const updateIndicator = () => {
      indicatorFrame = 0;
      const active = list?.querySelector('a[aria-current="location"]');
      if (!indicator) return;
      indicator.classList.toggle('is-visible', !!active);
      if (active) {
        indicator.style.setProperty('--index-y', `${active.offsetTop}px`);
        indicator.style.setProperty('--index-height', `${active.offsetHeight}px`);
      }
    };
    const scheduleIndicator = () => {
      if (!indicatorFrame) indicatorFrame = requestAnimationFrame(updateIndicator);
    };
    const settleMotion = () => { if (!allowed()) current?.getAnimations().forEach(animation => animation.cancel()); };
    reduced.addEventListener('change', settleMotion, on);
    document.addEventListener('uni:motionchange', settleMotion, on);
    const mutation = new MutationObserver(records => {
      if (records.some(record => record.attributeName === 'aria-current')) {
        scheduleIndicator();
        if (allowed() && current && matchMedia('(max-width: 999px)').matches) {
          current.getAnimations().forEach(animation => animation.cancel());
          current.animate([{ opacity: .45, transform: 'translateY(3px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
        }
      }
    });
    if (list) mutation.observe(list, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleIndicator) : null;
    if (list) resize?.observe(list);
    root.querySelector('[data-program-index]')?.addEventListener('toggle', scheduleIndicator, on);
    document.fonts?.ready.then(() => { if (root.isConnected) scheduleIndicator(); });
    scheduleIndicator();
    const competencies = [...root.querySelectorAll('[data-competency]')];
    const visible = new Set();
    let pointerItem = null;
    let activeSkill = null;
    const chooseSkill = item => {
      if (activeSkill === item) return;
      activeSkill?.classList.remove('is-reading');
      activeSkill = item;
      activeSkill?.classList.add('is-reading');
    };
    const viewportSkill = () => {
      if (pointerItem) return;
      const mid = innerHeight * .38;
      const next = [...visible].sort((a, b) => Math.abs(a.getBoundingClientRect().top - mid) - Math.abs(b.getBoundingClientRect().top - mid))[0];
      chooseSkill(next || null);
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      entries.forEach(entry => entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target));
      viewportSkill();
    }, { rootMargin: '-24% 0px -40% 0px', threshold: [0, .5] }) : null;
    competencies.forEach(item => {
      observer?.observe(item);
      item.addEventListener('pointerenter', () => { if (hover.matches) { pointerItem = item; chooseSkill(item); } }, on);
      item.addEventListener('pointerleave', () => { pointerItem = null; viewportSkill(); }, on);
    });
    root.querySelectorAll('.uni-program-term').forEach(term => {
      term.addEventListener('keydown', event => {
        if (event.key === 'Escape' && term.open && !term.dataset.accordionState) { term.open = false; term.querySelector('summary')?.focus(); }
      }, on);
    });
    const invitation = root.querySelector('[data-program-brief]');
    const dialog = invitation?.querySelector('[data-brief-dialog]');
    const trigger = invitation?.querySelector('[data-brief-open]');
    let priorOverflow = '';
    let scrollLocked = false;
    let printCopy = null;
    let printing = false;
    const closeBrief = () => dialog?.close();
    const cleanupPrint = () => {
      if (!printing) return;
      printing = false;
      printCopy?.remove();
      printCopy = null;
      document.body.classList.remove('uni-program-brief-printing');
      if (dialog?.isConnected && typeof dialog.showModal === 'function') {
        dialog.showModal();
        lockScroll();
        dialog.querySelector('[data-brief-print]')?.focus();
      }
    };
    const lockScroll = () => {
      if (scrollLocked) return;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      scrollLocked = true;
    };
    const unlockScroll = () => {
      if (!scrollLocked) return;
      document.body.style.overflow = priorOverflow;
      scrollLocked = false;
    };
    if (dialog && trigger && typeof dialog.showModal === 'function') {
      trigger.hidden = false;
      invitation.classList.add('is-ready');
      trigger.addEventListener('click', () => {
        const date = dialog.querySelector('[data-brief-date]');
        const now = new Date();
        if (date) {
          date.dateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          date.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'es', { year: 'numeric', month: 'long', day: 'numeric' }).format(now);
        }
        dialog.showModal();
        lockScroll();
      }, on);
      dialog.querySelector('[data-brief-close]')?.addEventListener('click', closeBrief, on);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeBrief();
      }, on);
      dialog.addEventListener('close', () => { if (dialog.open) return; unlockScroll(); if (!printing && trigger.isConnected) trigger.focus({ preventScroll: true }); }, on);
      dialog.querySelector('[data-brief-share]')?.addEventListener('click', async () => {
        const payload = { title: dialog.dataset.shareTitle, url: dialog.dataset.shareUrl };
        const status = dialog.querySelector('[data-brief-status]');
        try {
          if (navigator.share) await navigator.share(payload);
          else { await navigator.clipboard.writeText(payload.url); status.textContent = dialog.dataset.copySuccess; }
        } catch (error) {
          if (error.name !== 'AbortError') status.textContent = dialog.dataset.copyError;
        }
      }, on);
      dialog.querySelector('[data-brief-print]')?.addEventListener('click', async event => {
        const button = event.currentTarget;
        if (printing || button.disabled) return;
        button.disabled = true;
        try {
          const original = dialog.querySelector('[data-program-brief-document]');
          await Promise.all([...original.querySelectorAll('img')].map(img => { img.loading = 'eager'; return img.decode?.().catch(() => {}) || Promise.resolve(); }));
          if (!dialog.isConnected || !dialog.open) return;
          printCopy = original.cloneNode(true);
          printCopy.removeAttribute('data-program-brief-document');
          printCopy.classList.add('uni-program-brief-print-copy');
          printCopy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          printCopy.setAttribute('aria-hidden', 'true');
          document.body.append(printCopy);
          printing = true;
          dialog.close();
          unlockScroll();
          document.body.classList.add('uni-program-brief-printing');
          window.print();
        } catch (_) { cleanupPrint(); } finally { button.disabled = false; }
      }, on);
      window.addEventListener('afterprint', cleanupPrint, on);
    }
    instances.set(root, () => {
      controller.abort(); mutation.disconnect(); resize?.disconnect(); observer?.disconnect();
      if (indicatorFrame) cancelAnimationFrame(indicatorFrame);
      printing = false; printCopy?.remove(); document.body.classList.remove('uni-program-brief-printing');
      if (dialog?.open) dialog.close(); unlockScroll();
    });
  }
  const within = scope => {
    if (scope instanceof Element && scope.matches('uni-program')) init(scope);
    scope.querySelectorAll('uni-program').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => within(document), { once: true });
  else within(document);
  document.addEventListener('shopify:section:load', event => within(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
  });
})();
